/**
 * מסך הכניסה  —  /login
 * כל כתובת שהמשתמש אינו מחובר בה מפנה לכאן. אחרי כניסה - /create-lead.
 */
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import { getApiError } from '../utils/apiError';

// The backend answers a login that needs a TOTP code with 401 +
// detail.message === "2FA code is required" (see psy-clinic auth router).
const isTwoFactorRequired = (err: any) =>
  err?.response?.status === 401 && err?.response?.data?.detail?.message === '2FA code is required';

const translateLoginError = (err: any): string => {
  // A request that never reached the server is not a wrong password, and
  // saying so costs the coordinator the wrong ten minutes: she retypes a
  // password that was right, then calls to report an account problem that
  // does not exist. axios leaves `response` undefined for exactly this -
  // the server down, a deploy mid-flight, a blocked origin.
  if (!err?.response) {
    return navigator.onLine === false
      ? 'נראה שאין חיבור לאינטרנט. בדקי את החיבור ונסי שוב.'
      : 'לא ניתן להתחבר לשרת כרגע. נסי שוב בעוד רגע.';
  }
  const raw = getApiError(err, '');
  // getApiError already translates a detail it knows; anything it does not
  // is not worth showing on a login screen, where the useful answer is
  // almost always the same one.
  return raw && /[\u0590-\u05FF]/.test(raw) ? raw : 'ההתחברות נכשלה, נא לבדוק את הפרטים';
};

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpToken, setTotpToken] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('נא להזין כתובת דוא"ל תקינה');
      return;
    }
    if (!password) {
      setError('נדרשת סיסמה');
      return;
    }

    setSubmitting(true);
    try {
      await login(email, password, needsTotp ? totpToken : undefined);
    } catch (err: any) {
      if (isTwoFactorRequired(err)) {
        setNeedsTotp(true);
        setError('');
      } else {
        // The server answers in English and this screen is the coordinator's
        // first one; the two refusals she can actually reach are said in
        // Hebrew rather than passed through.
        setError(translateLoginError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card dir="rtl" sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.dark', mb: 0.5 }}>
            Taliaz — פורטל הקופות
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            התחברות למערכת
          </Typography>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                label='דוא"ל'
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                fullWidth
                autoFocus
              />
              <TextField
                label="סיסמה"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                fullWidth
                InputProps={{
                  startAdornment: (
                    // Disabled while the field is empty, so pressing it before
                    // typing reads as "nothing to show yet" rather than as a
                    // toggle that did not take - which is how it read when the
                    // button was live but had no effect anyone could see.
                    <InputAdornment position="start">
                      <Tooltip title={showPassword ? 'הסתרת הסיסמה' : 'הצגת הסיסמה'}>
                        <span>
                          <IconButton
                            type="button"
                            onClick={() => setShowPassword((shown) => !shown)}
                            aria-label={showPassword ? 'הסתרת הסיסמה' : 'הצגת הסיסמה'}
                            disabled={!password}
                            edge="start"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              {needsTotp && (
                <TextField
                  label="קוד אימות"
                  value={totpToken}
                  onChange={(e) => setTotpToken(e.target.value)}
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  helperText="הזינו את הקוד בן 6 הספרות מאפליקציית האימות"
                  fullWidth
                  autoFocus
                />
              )}

              <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth>
                {submitting ? 'מתחבר…' : 'כניסה'}
              </Button>

              <Link component={RouterLink} to="/forgot-password" variant="body2" textAlign="center">
                שכחת סיסמה?
              </Link>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
